import axios from "axios";
import { prepareNotificationSignOut } from "@/services/notificationDeviceSession";
import * as Sentry from "@sentry/vue";
import { jwtDecode } from "jwt-decode";
import { isAPIErrorStatus, isNetworkError } from "@/services/apiErrors";
import { normalizeCustomerID } from "@/classes/customerID";
import { apiSessionPaused, getAPISessionRevision, pauseAPISession, resumeAPISession } from "@/services/apiSession";

// Import store directly from its source to avoid circular dependency
import { store } from "@/store";
import {
    setTokenInStorage,
    getTokenInStorage,
    removeTokenInStorage,
    setRefreshTokenInStorage,
    getRefreshTokenInStorage,
    removeRefreshTokenInStorage
} from "@/classes/MobileStorage.js";

// Module-level single-flight guard for token refresh (F8) — shared across all
// AuthService instances and both the axios interceptor and the fetch path
// *within one tab*. Cross-tab single-flight is handled separately via the Web
// Locks API in refreshAccessToken().
let pendingRefresh = null;
let pendingRefreshRevision;

function assertCurrentSession(revision, requireActive = true) {
    if (revision !== getAPISessionRevision() || (requireActive && apiSessionPaused.value)) {
        throw new axios.CanceledError("Authentication session changed");
    }
}

// Cross-tab refresh lock name (Web Locks API). Refresh tokens are single-use and
// rotate on every refresh, so two tabs refreshing at once would race: one wins,
// the other gets a 401 and used to force a logout that also wiped the winner's
// freshly-stored token. Serializing refreshes across tabs removes the race.
const REFRESH_LOCK = "forkto-token-refresh";

// Never wait on the cross-tab refresh lock forever: a wedged sibling tab (one
// frozen by the browser, paused in devtools, or sitting in a slow refresh
// POST) would otherwise park every 401'd request in this tab on a promise
// that never settles — the page spins indefinitely while the rest of the app
// works. Past this deadline we give up on coordination and refresh anyway;
// the rotation race the lock exists to prevent is already survivable via
// _adoptRotatedToken (both before POSTing and on a losing-race 401).
const REFRESH_LOCK_WAIT_TIMEOUT_MS = 15000;

// Treat an access token as still usable if it decodes and its exp is comfortably
// in the future. Used to detect that a sibling tab already rotated the token
// while we waited, so we can adopt it instead of spending our (now single-use)
// refresh token on a doomed POST.
const ACCESS_TOKEN_SKEW_SECONDS = 10;
function isAccessTokenFresh(token) {
    if (!token || token === "undefined") return false;
    try {
        const { exp } = jwtDecode(token);
        if (!exp) return false;
        return Math.round(Date.now() / 1000) + ACCESS_TOKEN_SKEW_SECONDS < exp;
    } catch {
        return false;
    }
}

// Define userIsRole as a standalone function that can be imported directly
export function userIsRole(role) {
    return store.getters.userRole() === role;
}

// Credential POSTs (login / quick-login) are the first network hop of a sign-in
// and stall often enough on flaky connections that a single timeout bounces the
// user straight back to the form. Retry them on a *connectivity* failure only —
// a timeout or network error that produced NO HTTP response. A 401 (wrong
// password / PIN) carries a response, so isNetworkError() is false and it is
// never retried: credentials still fail fast and the PIN-lockout counter is
// never inflated by a retry. A shorter per-attempt timeout lets a stalled
// attempt give up well before the global 30s so the retry can land while the
// user is still watching the spinner.
const LOGIN_ATTEMPT_TIMEOUT_MS = 15000;
const LOGIN_MAX_RETRIES = 2;
const LOGIN_RETRY_BASE_DELAY_MS = 500;

async function postCredentialsWithRetry(url, body, config = {}) {
    for (let attempt = 0; ; attempt++) {
        const isFinalAttempt = attempt === LOGIN_MAX_RETRIES;
        try {
            return await axios.post(url, body, {
                ...config,
                timeout: LOGIN_ATTEMPT_TIMEOUT_MS,
                // Don't report attempts we're about to retry to Sentry; only a
                // final, retries-exhausted failure is worth capturing.
                skipSentry: !isFinalAttempt
            });
        } catch (err) {
            if (isFinalAttempt || !isNetworkError(err)) throw err;
            await new Promise((resolve) =>
                setTimeout(resolve, LOGIN_RETRY_BASE_DELAY_MS * (attempt + 1))
            );
        }
    }
}

export class AuthService {
    constructor(emitter) {
        this.emitter = emitter;
        this.baseUrl = import.meta.env.VITE_API_URL;
        this.isLoggingIn = false;
    }

    setLoggingIn(value) {
        this.isLoggingIn = value;
    }

    async clearAuth() {
        pauseAPISession();
        await prepareNotificationSignOut().catch(() => {});
        await removeTokenInStorage();
        await removeRefreshTokenInStorage();
        localStorage.removeItem("leased_date");
        localStorage.removeItem("location_id");
        localStorage.removeItem("department_id");
        localStorage.removeItem("multiple_locations");
        localStorage.removeItem("multiple_departments");
        localStorage.removeItem("easy_user_id");
        localStorage.removeItem("devmode");
        // Keep the local tenant selection across the forced re-login caused by
        // switching tenants. Explicitly exiting the Dev Panel still uses
        // clearDevOverrides and removes the tenant override.
        store.commit("clearDevSessionOverrides");
        store.commit("setToken", null);
        store.commit("resetTenantState");
        axios.defaults.headers.common["Authorization"] = "";
        Sentry.setUser(null);

        // Clear biometric credentials on logout
        if (store.state.isMobileOS) {
            try {
                const { BiometricService } = await import("@/services/BiometricService.js");
                await BiometricService.unenroll();
            } catch {
                // Non-critical
            }
        }
    }

    setAxiosToken(token) {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    }

    getUserIDFromToken(token) {
        if (!token) return null;
        try {
            const decoded = jwtDecode(token);
            return decoded.id || null;
        } catch {
            return null;
        }
    }

    async isTokenValid(token) {
        try {
            const response = await axios.get(`${this.baseUrl}/verify-token`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            return response.status === 200;
        } catch (error) {
            return false;
        }
    }

    async refreshAccessToken() {
        const revision = getAPISessionRevision();
        assertCurrentSession(revision);
        // Single-flight guard (F8): coalesce concurrent refreshes within one tab
        // so a rotating refresh token isn't double-spent. Without this, parallel
        // 401s from the axios interceptor and the fetch path each POST
        // /refresh-token; all but the first fail against the now-rotated token.
        if (pendingRefresh && pendingRefreshRevision === revision) {
            return pendingRefresh;
        }
        pendingRefreshRevision = revision;
        const refresh = this._refreshWithLock(revision).finally(() => {
            if (pendingRefresh === refresh) pendingRefresh = null;
        });
        pendingRefresh = refresh;
        return pendingRefresh;
    }

    // Serialize refreshes across tabs. Two tabs whose access tokens expire in the
    // same second would otherwise each POST the shared refresh token; only one
    // can win the server's single-use rotation, and the loser's 401 used to force
    // a logout that wiped the winner's freshly-stored token too. The Web Locks API
    // ensures only one tab refreshes at a time; the rest wait and then find the
    // already-rotated token in _doRefreshAccessToken's pre-check.
    async _refreshWithLock(revision = getAPISessionRevision()) {
        assertCurrentSession(revision);
        if (!navigator?.locks?.request) {
            return this._doRefreshAccessToken(revision);
        }

        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REFRESH_LOCK_WAIT_TIMEOUT_MS);
        try {
            return await navigator.locks.request(
                REFRESH_LOCK,
                { signal: controller.signal },
                () => {
                    // Lock granted — disarm the timeout so it can't abort a
                    // refresh that is actually in flight.
                    clearTimeout(timer);
                    return this._doRefreshAccessToken(revision);
                }
            );
        } catch (error) {
            // A rejected refresh may have cleared auth itself. Preserve that
            // rejection so its caller can finish the transition to login.
            if (!controller.signal.aborted) throw error;
            assertCurrentSession(revision);
            // Snapshot lock state so Sentry shows WHICH client is wedged
            // holding the lock, not just that the wait timed out.
            let lockState = null;
            try {
                lockState = await navigator.locks.query?.();
            } catch {
                // query() unsupported — the breadcrumb is still useful without it.
            }
            Sentry.addBreadcrumb({
                category: "auth",
                message: "Refresh lock wait timed out — refreshing without cross-tab lock",
                level: "warning",
                data: {
                    held: lockState?.held?.map((l) => `${l.name} by ${l.clientId}`),
                    pending: lockState?.pending?.map((l) => `${l.name} by ${l.clientId}`)
                }
            });
            // The lock holder may have finished storing a rotated token just
            // before wedging; adopt it rather than double-spending ours.
            const adopted = await this._adoptRotatedToken(revision);
            if (adopted) return adopted;
            return this._doRefreshAccessToken(revision);
        } finally {
            clearTimeout(timer);
        }
    }

    async _doRefreshAccessToken(revision = getAPISessionRevision()) {
        assertCurrentSession(revision);
        // A sibling tab may have rotated the token while we waited for the lock.
        // If the stored access token is now fresh, adopt it instead of spending
        // our (already-rotated, single-use) refresh token on a POST that would
        // lose the race and 401.
        const adopted = await this._adoptRotatedToken(revision);
        if (adopted) return adopted;

        const refreshToken = await getRefreshTokenInStorage();
        assertCurrentSession(revision);
        if (!refreshToken) {
            Sentry.addBreadcrumb({
                category: "auth",
                message: "Forced logout: no refresh token in storage",
                level: "warning"
            });
            await this.clearAuth();
            throw new Error("No refresh token available");
        }

        try {
            const { accessToken } = await this.exchangeRefreshToken(refreshToken, revision);
            return accessToken;
        } catch (error) {
            assertCurrentSession(revision);
            // Only clear auth if the server explicitly rejected the refresh token.
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                // Fallback for environments without the Web Locks API (or any race
                // that slips past it): before tearing down the session, check
                // whether a concurrent refresh already rotated the token. If so,
                // adopt it rather than logging everyone out over a benign rotation
                // race that the server reports as `token_race_condition`.
                const adoptedOnFailure = await this._adoptRotatedToken(revision);
                if (adoptedOnFailure) return adoptedOnFailure;
                await this.clearAuth();
            }
            throw error;
        }
    }

    // If a sibling tab has rotated the stored access token to a different, still-
    // fresh value, sync it into axios + the store and return it. Returns null when
    // there's nothing newer to adopt (the caller must then refresh itself).
    // Requiring the stored token to differ from this tab's current token avoids
    // re-adopting the very token a 401 just rejected. Used both before POSTing and
    // on a refresh 401 to recover from a concurrent cross-tab rotation.
    async _adoptRotatedToken(revision = getAPISessionRevision()) {
        assertCurrentSession(revision);
        const stored = await getTokenInStorage();
        assertCurrentSession(revision);
        if (stored === store.state.token) return null;
        if (!isAccessTokenFresh(stored)) return null;
        this.setAxiosToken(stored);
        store.commit("setToken", stored);
        return stored;
    }

    async exchangeRefreshToken(refreshToken, revision = getAPISessionRevision(), { allowPaused = false } = {}) {
        assertCurrentSession(revision, !allowPaused);
        const response = await axios.post(
            `${this.baseUrl}/refresh-token`,
            { refresh_token: refreshToken },
            {
                headers: {
                    Authorization: "" // No auth needed for refresh
                }
            }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;
        assertCurrentSession(revision, !allowPaused);
        await this.setTokens(access_token, newRefreshToken);

        return {
            accessToken: access_token,
            refreshToken: newRefreshToken
        };
    }

    async unlockWithBiometrics() {
        const { BiometricService } = await import("@/services/BiometricService.js");
        const { refreshToken, userID } = await BiometricService.authenticate();
        const { accessToken, refreshToken: newRefreshToken } =
            await this.exchangeRefreshToken(refreshToken, getAPISessionRevision(), { allowPaused: true });

        // Reject a token issued for another tenant. A device can hold one
        // customer domain at a time, so cross-tenant biometric unlock is invalid.
        if (!store.getters.isTokenValid()) {
            await this.clearAuth();
            throw new Error("Biometric token tenant mismatch");
        }

        const resolvedUserID = userID || this.getUserIDFromToken(accessToken);
        await BiometricService.updateRefreshToken(newRefreshToken || refreshToken, resolvedUserID);
        await this.setUserData(resolvedUserID, accessToken);
        store.commit("setAuthUnlockRequired", false);

        return {
            accessToken,
            userID: resolvedUserID
        };
    }

    async setTokens(accessToken, refreshToken) {
        this.setAxiosToken(accessToken);
        store.commit("setToken", accessToken);
        await setTokenInStorage(accessToken);
        if (refreshToken) {
            await setRefreshTokenInStorage(refreshToken);

            // Keep biometric keychain in sync with rotated tokens
            if (store.state.isMobileOS) {
                try {
                    const { BiometricService } = await import("@/services/BiometricService.js");
                    await BiometricService.updateRefreshToken(refreshToken);
                } catch {
                    // Non-critical
                }
            }
        }
    }

    async checkAuthWithoutLogout() {
        if (!store.getters.isTokenValid()) {
            return false;
        }
        if (store.getters.userID() == null) {
            return false;
        }
        return true;
    }

    // Confirms a customer domain before sign-in and returns { subdomain,
    // business_name }. Resolves { exists: false } for a domain the backend does
    // not know. Any other failure (offline, an older backend without the
    // route) resolves { exists: true } with no name so the domain step is
    // never blocked by the lookup itself.
    async lookupTenant(customerID) {
        const subdomain = normalizeCustomerID(customerID);
        if (!subdomain) return { exists: false, subdomain, business_name: "" };

        try {
            const response = await axios.get(`${this.baseUrl}/tenant`, {
                headers: { "Customer-ID": subdomain, Authorization: "" },
                timeout: LOGIN_ATTEMPT_TIMEOUT_MS,
                skipSentry: true
            });
            const data = response.data?.data ?? {};
            return {
                exists: true,
                subdomain: data.subdomain || subdomain,
                business_name: data.business_name || ""
            };
        } catch (err) {
            if (isAPIErrorStatus(err, 400)) {
                return { exists: false, subdomain, business_name: "" };
            }
            return { exists: true, subdomain, business_name: "" };
        }
    }

    async login(username, password) {
        const userLogin = username.includes("@")
            ? { email: username, password }
            : { username, password };

        const response = await postCredentialsWithRetry(`${this.baseUrl}/login`, userLogin);
        return response.data;
    }

    async quickLogin(userID, pin) {
        const response = await postCredentialsWithRetry(
            `${this.baseUrl}/quick-login`,
            { id: userID, pin },
            {
                headers: {
                    Authorization: ""
                }
            }
        );
        return response.data;
    }

    async setUserData(userID, token) {
        resumeAPISession(JSON.stringify([store.state.customerID, this.getUserIDFromToken(token)]));
        const sessionRevision = getAPISessionRevision();
        this.setAxiosToken(token);
        localStorage.setItem("leased_date", Date.now());
        store.commit("setToken", token);
        store.commit("setAuthUnlockRequired", false);

        try {
            await setTokenInStorage(token);
        } catch (error) {
            console.error("Failed to set token in storage:", error);
        }
        assertCurrentSession(sessionRevision);

        let user = null;
        if (userID !== import.meta.env.VITE_ADMIN_ID) {
            try {
                user = await this.User.GetMe();
                assertCurrentSession(sessionRevision);
                store.commit("setUser", user);
            } catch (error) {
                assertCurrentSession(sessionRevision);
                console.error("Failed to get user data:", error);
                await this.clearAuth();
                this.emitter.emit("logout");
                throw error;
            }
        } else {
            Sentry.setUser({ id: "admin-override", username: "admin-override" });
            Sentry.setTag("admin_override", "true");
        }

        if (!userIsRole("supplier")) {
            await this.setLocationData(user);
            await this.setDepartmentData(user);
        }

        if (user) {
            this.emitter.emit("user-location-data-loaded");
            this.emitter.emit("user-department-data-loaded");
        }

        return user;
    }

    async logout() {
        if (this.isLoggingIn) return;

        pauseAPISession();
        const refreshToken = await getRefreshTokenInStorage();
        const logoutTenant = store.state.customerID;
        const notificationSignOut = await prepareNotificationSignOut().catch(() => ({}));
        const remoteCleanup = [Promise.resolve().then(() => this.User.SetActiveStatus(false))];
        if (refreshToken) {
            remoteCleanup.push(
                Promise.resolve().then(() =>
                    axios.post(
                        `${this.baseUrl}/logout`,
                        { refresh_token: refreshToken, ...notificationSignOut },
                        {
                            headers: {
                                Authorization: "", // No auth needed
                                "Customer-ID": logoutTenant
                            }
                        }
                    )
                )
            );
        }
        const remoteCleanupPromise = Promise.allSettled(remoteCleanup);

        // Local sign-out is the user-visible security boundary. Do it before
        // waiting on connectivity so an offline or slow server cannot strand the
        // browser/native app on an authenticated screen.
        await this.clearAuth();
        this.emitter.emit("logout");

        // Presence and refresh-token revocation are best-effort. Both requests
        // have already captured the current credentials and may finish after the
        // login page is shown.
        void remoteCleanupPromise;
    }

    async requestPasswordReset(email) {
        // A reset request does not change a password or confirm email delivery.
        // Its caller owns feedback; password-change listeners belong to actual changes.
        const response = await axios.post(`${this.baseUrl}/request-reset-password`, { email });
        return response.data.data;
    }

    async resetPassword(token, newPassword) {
        try {
            const response = await axios.post(
                `${this.baseUrl}/reset-password`,
                { token, new_password: newPassword },
                {
                    headers: {
                        Authorization: "" // No auth needed for password reset
                    }
                }
            );
            this.emitter.emit("password-change-success", response.data.data);
            return response.data.data;
        } catch (error) {
            this.emitter.emit("password-change-error", error);
            throw error;
        }
    }

    async changePassword(data) {
        try {
            const response = await axios.post(`${this.baseUrl}/change-password`, data);
            this.emitter.emit("password-change-success", response.data.data);
            return response.data.data;
        } catch (error) {
            this.emitter.emit("password-change-error", error);
            throw error;
        }
    }

    // Helper methods for location and department data
    async setLocationData(user) {
        const locationList = await this.Location.List();

        if (!locationList || locationList.length < 1) {
            return;
        }

        const storedLocationID = localStorage.getItem("location_id");
        let locationID;

        const getActiveLocation = (locations) => {
            return locations.find((l) => !l.tags?.includes("DEACTIVATED")) || locations[0];
        };

        if (user) {
            if (user.location_ids?.length > 0) {
                const userLocations = locationList.filter((l) => user.location_ids.includes(l.id));
                const activeUserLocation = getActiveLocation(userLocations);

                if (
                    storedLocationID &&
                    userLocations.some(
                        (l) => l.id === storedLocationID && !l.tags?.includes("DEACTIVATED")
                    )
                ) {
                    locationID = storedLocationID;
                } else {
                    locationID = activeUserLocation.id;
                }
                localStorage.setItem("multiple_locations", user.location_ids.length > 1);
            }
        } else {
            const activeLocations = locationList.filter((l) => !l.tags?.includes("DEACTIVATED"));
            locationID =
                storedLocationID && activeLocations.some((l) => l.id === storedLocationID)
                    ? storedLocationID
                    : getActiveLocation(locationList).id;
            localStorage.setItem("multiple_locations", true);
        }

        if (locationID) {
            store.commit("setLocationByID", locationID);
        }
    }

    async setDepartmentData(user) {
        if (user) {
            if (user.department_ids?.length > 0) {
                const storedDepartmentID = localStorage.getItem("department_id");
                const departmentID =
                    storedDepartmentID && user.department_ids.includes(storedDepartmentID)
                        ? storedDepartmentID
                        : user.department_ids[0];
                store.commit("setDepartmentByID", departmentID);
                localStorage.setItem("multiple_departments", user.department_ids.length > 1);
            } else {
                store.commit("setDepartmentByID", "");
                localStorage.removeItem("department_id");
                localStorage.removeItem("multiple_departments");
            }
        } else {
            const departmentList = await this.Department.List();
            if (departmentList?.length > 0) {
                const storedDepartmentID = localStorage.getItem("department_id");
                const departmentID = departmentList.some((d) => d.id === storedDepartmentID)
                    ? storedDepartmentID
                    : departmentList[0].id;
                store.commit("setDepartmentByID", departmentID);
                localStorage.setItem("multiple_departments", true);
            } else {
                store.commit("setDepartmentByID", "");
                localStorage.removeItem("department_id");
                localStorage.removeItem("multiple_departments");
            }
        }
    }
}
